// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Unified Task Management types.
 * Collection: grc_tasks, grc_task_audit
 * Sprint 17.0.0: Unified Task Data Model & Backend Foundation
 */
/**
 * Valid status transitions map. Terminal statuses have no outgoing transitions.
 * Shared between backend enforcement and frontend UI validation.
 * Sprint 25.8.0: Exported so TaskBoardPage can enforce client-side transition rules.
 */
export const TASK_VALID_TRANSITIONS = {
    // Sprint 63.30.0 (E10, #574) — `escalated` is reachable from pending,
    // in-progress and blocked: an SLA breach or a person escalates a task that has
    // not started or is stuck, not only one being worked. Moving a task to
    // `escalated` needs `tm:task:escalate` (the route checks it).
    pending: ['in_progress', 'escalated', 'cancelled'],
    in_progress: ['completed', 'blocked', 'escalated', 'cancelled'],
    blocked: ['in_progress', 'escalated', 'cancelled'],
    escalated: ['in_progress', 'completed', 'cancelled'],
    completed: [],
    cancelled: [],
    overdue: [], // computed status, not stored
};
/** Terminal statuses that cannot transition to any other status. */
export const TASK_TERMINAL_STATUSES = ['completed', 'cancelled'];
/**
 * Sprint 64.120.0 — `TaskStatus` as a value, because a union is erased at run
 * time and a consumer that needs to validate against its members transcribes
 * them.
 *
 * It had been transcribed, wrongly: `skills/handlers/update-task.ts` carried
 * `new Set(['open', 'in_progress', 'done', 'blocked'])`, two of whose four
 * members are in no `TaskStatus` — so the handler wrote a second vocabulary
 * into `grc_tasks` and nothing compared the two. That is idea-catalog #100's
 * mechanism (Sprint 64.45.0) in this union.
 *
 * The array is declared beside the union rather than the union derived from it,
 * so each member keeps the comment saying what it means — and **exactness is
 * proved in both directions** below rather than assumed, because `satisfies`
 * alone would accept a list missing a member.
 */
export const TASK_STATUSES = [
    'pending',
    'in_progress',
    'blocked',
    'completed',
    'overdue',
    'escalated',
    'cancelled',
];
/** Every listed member is a `TaskStatus` — the direction `satisfies` gives. */
const _taskStatusesAreStatuses = TASK_STATUSES;
/** …and every `TaskStatus` is listed — the direction it does not. */
const _taskStatusesAreComplete = null;
void _taskStatusesAreStatuses;
void _taskStatusesAreComplete;
// ── Task Priority ──
/** Runtime vocabulary; the type is derived from it so consumers order by it without re-typing it. */
export const TASK_PRIORITIES = ['critical', 'high', 'medium', 'low'];
/**
 * Every `TaskSourceModule`, as a value.
 *
 * Sprint 65.1.0. This did not exist, so `modules/task-management/schemas.ts`
 * carried an 18-member transcription of the union above and validated
 * `POST /tasks` against it. That is the identical mechanism Sprint 64.120.0
 * fixed one field away, in the same object literal, for `TASK_TYPES` — its note
 * there reads *"a validator narrower than its union is a reachability gap by
 * construction"*, and `TASK_SOURCE_MODULES` was the remaining instance. Adding
 * `compliance_operations` to the union alone would have type-checked everywhere
 * and been refused at the API by a copy nothing pinned.
 *
 * Unlike `TASK_STATUSES`, which the backend narrows deliberately (it omits the
 * computed `'overdue'`), there is no member of this union the API should refuse
 * — `'manual'` included — so the validator imports this directly.
 *
 * Proved in BOTH directions below, because `satisfies` alone would accept a list
 * missing a member — the `TASK_STATUSES` contract, three declarations up.
 */
export const TASK_SOURCE_MODULES = [
    'rights_requests',
    'assessments',
    'incidents',
    'incident_ops',
    'monitoring',
    'vendor_risk',
    'governance',
    'deletion_orchestration',
    'access_review',
    'consent',
    'consent_properties',
    'forms',
    'localization',
    'program_governance',
    'privacy_scanner',
    'classification',
    'discovery',
    'data_mapping',
    'compliance_operations',
    'training',
    'privacy_office',
    'authority_operations',
    'ai_governance',
    'regulatory_classification',
    'org_settings',
    'customs_tax_administration',
    'service_certificates',
    'manual',
];
/** Every listed member is a `TaskSourceModule` — the direction `satisfies` gives. */
const _taskSourceModulesAreModules = TASK_SOURCE_MODULES;
/** …and every `TaskSourceModule` is listed — the direction it does not. */
const _taskSourceModulesAreComplete = null;
void _taskSourceModulesAreModules;
void _taskSourceModulesAreComplete;
/**
 * Sprint 64.120.0 — `TaskType` as a value, for the reason `TASK_STATUSES` is.
 *
 * `task-management/schemas.ts` carried a hand-typed copy of these 34 members,
 * and adding `forseti_task` to the union above **did not add it to the
 * validator** — so `POST /tasks` would have refused the very `task_type` this
 * sprint introduced, and the schema would have been one member narrower than
 * the union it validates for. That is 64.45.0's AC7 exactly: *a validator
 * narrower than its union is a reachability gap by construction.*
 *
 * The gap was found by the re-derivation census going from 700 findings to 699
 * — the transcription stopped being an EXACT match and so stopped being
 * reported, which is the failure mode a set-based baseline hides. It is closed
 * by the schema importing this array rather than by adding one more line to the
 * copy.
 */
export const TASK_TYPES = [
    'dsr_case',
    'dsr_review',
    'dsr_escalation',
    'dpia_review',
    'dpia_approval',
    'transfer_review',
    'incident_task',
    'incident_notification',
    'playbook_step',
    'regulator_deadline',
    'monitoring_task',
    'kpi_alert',
    'vendor_review',
    'vendor_questionnaire',
    'governance_review',
    'drift_alert',
    'governance_approval',
    'deletion_task',
    'deletion_verification',
    'access_review',
    'sod_alert',
    'consent_workflow',
    'consent_alert',
    'property_review',
    'inconsistency_alert',
    'form_review',
    'dossier_deadline',
    'submission_task',
    'translation_review',
    'governance_task',
    'reporting_deadline',
    'scanner_alert',
    'classification_review',
    'lifecycle_task',
    'discovery_review',
    'compliance_obligation',
    'forseti_task',
    'training_assignment',
    'manual_task',
];
/** Every listed member is a `TaskType`. */
const _taskTypesAreTypes = TASK_TYPES;
/** …and every `TaskType` is listed — the direction `satisfies` does not give. */
const _taskTypesAreComplete = null;
void _taskTypesAreTypes;
void _taskTypesAreComplete;
export const TASK_SOURCE_KIND_FILTERS = [
    'manual',
    'module',
    'forseti_chat',
    'forseti_workflow_run',
];
//# sourceMappingURL=task-management.js.map